import {
	ChangeEvent,
	FunctionComponent,
	ReactNode,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faFilter, faSearch } from "@fortawesome/pro-regular-svg-icons";

import {
	FEED_LEAD_SCORE_FILTERS,
	FEED_SIGNAL_SCORE_FILTERS,
	FeedLeadScoreFilter,
	FeedSignalScoreFilter,
} from "@signalist/shared-types";

import { Icon } from "@/components";
import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";
import { SearchField } from "@/components/SearchField/SearchField";
import { ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import { ListSourceType } from "@/enum/list.enum";

import { FeedSelectedList } from "@/pages/Private/redux/feed/feed.slice";

import { FilterModal, IFilterWithValues } from "@/pages/Private/pages/List/components/FilterModal";
import { ListViewSwitcher } from "@/pages/Private/pages/List/components/ListViewSwitcher";

const SEARCH_DEBOUNCE_MS = 400;

/** The exclusive "everything" options of each score filter (list-page semantics). */
const EXCLUSIVE_OPTIONS = ["ALL", "ALL_WITH_PRIORITY", "ALL_WITH_SIGNAL"] as const;

export interface FeedFilterBarProps {
	/** Committed search value from the slice (local input debounces into it). */
	searchValue: string;
	onSearchChange: (value: string) => void;
	/** Lead-score filter (PriorityFilterStatus values); empty = all leads. */
	priorityFilterStatus: FeedLeadScoreFilter[];
	onPriorityFilterChange: (statuses: FeedLeadScoreFilter[]) => void;
	/** Signal-score filter (SignalFilterStatus values); empty = all signals. */
	signalFilterStatus: FeedSignalScoreFilter[];
	onSignalFilterChange: (statuses: FeedSignalScoreFilter[]) => void;
	/** The selected list when exactly one is selected — renders the List↔Feed switch (D4). */
	singleList: FeedSelectedList | null;
	/** Funnel-modal filter — the list page's FilterModal, single-list selections only. */
	filter: IFilterWithValues | null;
	onFilterChange: (filter: IFilterWithValues | null) => void;
	/** The AI queue pill (P6 D7) — injected so the bar stays presentation-only. */
	aiQueueSlot?: ReactNode;
}

/**
 * Resolves a checkbox-dropdown selection change with the list page's exclusive
 * semantics: adding an "everything" option (ALL / ALL_WITH_*) clears the rest;
 * picking a concrete score drops the "everything" pills. ALL maps to an empty
 * filter (UnassignedTable precedent).
 */
const resolveScoreSelection = <T extends string>(
	current: T[],
	values: DropdownWithCheckboxesItem[]
): T[] => {
	// Feed renders an empty filter as a checked ALL box, so treat empty as [ALL]
	// when detecting the just-clicked item — otherwise ALL looks "added" and any
	// concrete pick collapses straight back to ALL (PREC-3722).
	const effectiveCurrent = current.length ? current : (["ALL"] as T[]);
	const addedValue = values.find((value) => !effectiveCurrent.includes(value.id as T));

	if (addedValue && addedValue.id === "ALL") {
		return [];
	}

	if (
		addedValue &&
		EXCLUSIVE_OPTIONS.includes(addedValue.id as (typeof EXCLUSIVE_OPTIONS)[number])
	) {
		return [addedValue.id as T];
	}

	return values
		.filter((value) => !EXCLUSIVE_OPTIONS.includes(value.id as (typeof EXCLUSIVE_OPTIONS)[number]))
		.map((value) => value.id as T);
};

/**
 * L3 filter row of the Feed page: search, List↔Feed switch and the lead-score /
 * signal-score checkbox dropdowns — the same `DropdownWithCheckboxes` element
 * (and `inbox.*FilterOptions` labels) as the list-detail page. Sized like
 * `MessagesFilterBar` (44px row, 32px controls, `px-4` left edge).
 */
export const FeedFilterBar: FunctionComponent<FeedFilterBarProps> = ({
	searchValue,
	onSearchChange,
	priorityFilterStatus,
	onPriorityFilterChange,
	signalFilterStatus,
	onSignalFilterChange,
	singleList,
	filter,
	onFilterChange,
	aiQueueSlot,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [searchInput, setSearchInput] = useState(searchValue);
	const [showFilterModal, setShowFilterModal] = useState(false);
	const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

	const hasFilter = !!filter && Object.keys(filter).length > 0;

	useEffect(() => {
		setSearchInput(searchValue);
	}, [searchValue]);

	useEffect(() => {
		return () => {
			if (searchTimerRef.current) {
				clearTimeout(searchTimerRef.current);
			}
		};
	}, []);

	const handleSearchInput = (value: string) => {
		setSearchInput(value);

		if (searchTimerRef.current) {
			clearTimeout(searchTimerRef.current);
		}

		searchTimerRef.current = setTimeout(() => onSearchChange(value), SEARCH_DEBOUNCE_MS);
	};

	const priorityOptions = useMemo<DropdownWithCheckboxesItem[]>(
		() =>
			FEED_LEAD_SCORE_FILTERS.map((status) => ({
				id: status,
				title: t(`inbox.priorityFilterOptions.${status}`),
			})),
		[t]
	);

	const signalOptions = useMemo<DropdownWithCheckboxesItem[]>(
		() =>
			FEED_SIGNAL_SCORE_FILTERS.map((status) => ({
				id: status,
				title: t(`inbox.signalFilterOptions.${status}`),
			})),
		[t]
	);

	// An empty filter renders as the checked "ALL" pill (UnassignedTable precedent).
	const priorityValue = useMemo<DropdownWithCheckboxesItem[]>(
		() =>
			priorityFilterStatus.length > 0
				? priorityFilterStatus.map((status) => ({
						id: status,
						title: t(`inbox.priorityFilterOptions.${status}`),
					}))
				: [{ id: "ALL", title: t("inbox.priorityFilterOptions.ALL") }],
		[priorityFilterStatus, t]
	);

	const signalValue = useMemo<DropdownWithCheckboxesItem[]>(
		() =>
			signalFilterStatus.length > 0
				? signalFilterStatus.map((status) => ({
						id: status,
						title: t(`inbox.signalFilterOptions.${status}`),
					}))
				: [{ id: "ALL", title: t("inbox.signalFilterOptions.ALL") }],
		[signalFilterStatus, t]
	);

	return (
		<div className="flex items-center gap-2 h-11 w-full px-4 bg-white border-b border-border-light">
			<div className="w-[280px] flex-shrink-0">
				<SearchField
					className="!top-[9px] !left-[12px]"
					classNameInput="!h-[32px] !rounded-[10px] !text-xs !pl-9"
					handleChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchInput(e.target.value)}
					handleReset={() => handleSearchInput("")}
					icon={<Icon className="h-[14px]" icon={faSearch} />}
					iconPosition="left"
					name="feed-filter-search"
					placeholder={t("feed.searchPlaceholder")}
					value={searchInput}
				/>
			</div>

			{singleList && (
				// The Feed only ever shows TRACKER lists, so the Review option is hidden
				// here (matching the list detail switcher for Tracker lists, PREC-3584).
				<ListViewSwitcher
					activeView="feed"
					onSelectFeed={() => undefined}
					onSelectList={() => navigate(`/app/lists/detail/${singleList.id}`)}
					onSelectReview={() => undefined}
					showReview={false}
				/>
			)}

			{/* L3 order: signal score, lead score, funnel, queue pill — all inline left. */}
			<div className="w-fit min-w-[120px] max-w-[220px]">
				<DropdownWithCheckboxes
					data={signalOptions}
					defaultValue={signalValue}
					floating
					handleSelect={(values) =>
						onSignalFilterChange(resolveScoreSelection(signalFilterStatus, values))
					}
					portal
					size={ButtonSize.XS}
					testId="feed-signal-score-filter"
					value={signalValue}
				/>
			</div>

			<div className="w-fit min-w-[120px] max-w-[220px]">
				<DropdownWithCheckboxes
					data={priorityOptions}
					defaultValue={priorityValue}
					floating
					handleSelect={(values) =>
						onPriorityFilterChange(resolveScoreSelection(priorityFilterStatus, values))
					}
					portal
					size={ButtonSize.XS}
					testId="feed-lead-score-filter"
					value={priorityValue}
				/>
			</div>

			{/* Funnel → the list page's FilterModal; scoped to a single selected list.
			    Inline after the score dropdowns (not far right — the docked lead panel
			    overlays the bar's right edge and would hide it, PREC-3702). */}
			{singleList && (
				<button
					className={classNames(
						"flex h-[32px] w-[32px] shrink-0 cursor-pointer items-center justify-center rounded-[10px] border duration-200",
						hasFilter
							? "border-brand-light bg-light-blue text-brand-light"
							: "border-gray-200 bg-white text-text-secondary hover:border-brand-light"
					)}
					data-testid="feed-filter-modal-trigger"
					title={t("basics.filter")}
					type="button"
					onClick={() => setShowFilterModal(true)}
				>
					<Icon className="h-3.5 w-3.5" icon={faFilter} />
				</button>
			)}

			{/* AiQueuePill slot (P6 D7) — inline after the filters; hidden while empty. */}
			<div data-testid="feed-ai-queue-pill-slot">{aiQueueSlot}</div>

			{showFilterModal && singleList && (
				<FilterModal
					filter={filter ?? undefined}
					listId={singleList.id}
					listType={ListSourceType.TRACKER}
					setFilter={(action) =>
						onFilterChange(
							(typeof action === "function" ? action(filter ?? undefined) : action) ?? null
						)
					}
					setShowFilterModal={setShowFilterModal}
					showFilterModal={showFilterModal}
				/>
			)}
		</div>
	);
};
